import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon, type IconType } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { MailboxListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { client } from "@/hono";
import { EditMailboxModal } from "@/modals/mailboxes/edit-mailbox-modal";
import { MailboxEmailsModal } from "@/modals/mailboxes/mailbox-emails-modal";

export function RowActions({ row }: CellContext<MailboxListItem, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const asyncModal = useAsyncModal();
	const editMailboxModal = useDisclosure();
	const emailsModal = useDisclosure();

	/** Computed */
	const updateIsDenied = !!citadel && citadel.cannot("update", "Integration");
	const deleteIsDenied = !!citadel && citadel.cannot("delete", "Integration");

	/** Functions */
	async function handleOnPollNow() {
		const response = await client.mailboxes[":mailboxId"].poll.$post({
			param: { mailboxId: row.original.id },
		});
		await responseHandler(response);
		toast.success(t("mailboxes.poll_enqueued"));
	}

	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.mailboxes[":mailboxId"].$delete({
			param: { mailboxId: row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={`/organization/settings/mailboxes/${row.original.id}`}>
				<Menu
					items={createStandardMenuItems({
						items: [
							{
								label: t("mailboxes.poll_now"),
								icon: icon("RefreshCw") as IconType,
								onClick: handleOnPollNow,
								isDisabled: updateIsDenied,
							},
							{
								label: t("mailboxes.show_emails"),
								icon: icon("Mail") as IconType,
								onClick: emailsModal.onOpen,
							},
						],
						onEdit: editMailboxModal.onOpen,
						onEditOptions: { isDisabled: updateIsDenied },
						onDelete: handleOnDelete,
						onDeleteOptions: { isDisabled: deleteIsDenied },
					})}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
					/>
				</Menu>
			</RowActionsWrapper>
			{editMailboxModal.isOpen && (
				<EditMailboxModal
					isOpen={editMailboxModal.isOpen}
					onClose={editMailboxModal.onClose}
					mailboxId={row.original.id}
					onComplete={refetch}
				/>
			)}
			{emailsModal.isOpen && (
				<MailboxEmailsModal
					isOpen={emailsModal.isOpen}
					onClose={emailsModal.onClose}
					mailboxId={row.original.id}
					label={row.original.label}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t("mailboxes.delete_mailbox_confirm_message")}
					controller={asyncModal}
				/>
			)}
		</Fragment>
	);
}
